import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Clock, Heart, Send } from 'lucide-react';

import tableIconUserShare from '../../../assets/images/tabler-icon-user-share.svg';
import { getDateTimeFromNow } from '../../../helpers/commonHelper';
import { useReferralAccess } from '../../../hooks/useReferralAccess';
import { likeActivity, unlikeActivity } from '../../../redux/actions/dashboardActions';
import urls from '../../../routing/urls';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import ReferAccessButtonTooltip from '../../shared/ReferAccessButtonTooltip/ReferAccessButtonTooltip';

export const ActivityItem = ({ activity }) => {
  const { t } = useTranslation('dashboard');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organization = useSelector(selectCurrentOrganization);
  const { pathname } = useLocation();
  const accessOrganization = activity?.organization || organization;
  const { blocked, tooltipMessage } = useReferralAccess(accessOrganization);

  const renderActivityMessage = (organizationName, action, extraInfo = null) => (
    <p className="text-sm text-gray-600 mb-0">
      <span className="font-medium">{organizationName}</span> {action}
      {extraInfo && <span className="font-medium">{extraInfo}</span>}
    </p>
  );

  const navigateToJob = (jobUuid) => {
    if (blocked) {
      return;
    }

    localStorage.setItem('activeTab', 'browse');

    const url = pathname.includes('company') ? urls.companyReferral : urls.refer;
    navigate(`${url.replace(':companyName', organization?.general_setting_identifier)}?refer_for=${jobUuid}`);
  };

  const renderActivityContent = useMemo(() => {
    if (!activity) return null;

    switch (activity.action) {
      case 'bounty_amount_changed':
        return renderActivityMessage(activity.organization?.name, t('activity.rewardUpdated'));
      case 'closed':
        return renderActivityMessage(activity.organization?.name, t('activity.jobClosed'));
      case 'archived':
        return renderActivityMessage(activity.organization?.name, t('activity.jobClosed'));
      case 'published':
        return renderActivityMessage(
          activity.organization?.name,
          `${activity.activity_type === 'grouped_activity' ? t('activity.have') : t('activity.has')} `,
          t('activity.newOpenings', { count: activity.activity_type === 'grouped_activity' ? activity.job?.length : 1 })
        );
      case 'referral_has_sent':
        return renderActivityMessage(
          activity.user?.full_name === activity.current_user?.full_name ? t('activity.you') : activity.user?.full_name,
          t('activity.sentReferral')
        );
      case 'refer_candidate_hired':
        return renderActivityMessage(activity.organization?.name, t('activity.hiredFromReferral'));
      case 'unpublished':
        return renderActivityMessage(activity.organization?.name, t('activity.jobClosed'));
      case 'urgency_level_changed':
        return renderActivityMessage(activity.organization?.name, t('activity.urgencyChanged'));
      default:
        return renderActivityMessage('', activity.message || t('activity.update'));
    }
  }, [activity, t]);

  const handleLike = (activityId, activityLiked) => {
    if (activityLiked) {
      dispatch(unlikeActivity(activityId));
    } else {
      dispatch(likeActivity(activityId));
    }
  };

  return (
    <div className="p-3 transition-colors rounded-lg hover:bg-gray-50 hover:cursor-pointer">
      <div className="flex items-start gap-4">
        <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg">
          {(() => {
            if (activity?.action === 'referral_has_sent') {
              return <Send className="w-5 h-5 text-emerald-500" />;
            }
            if (activity?.action === 'published') {
              return <Building2 className="w-5 h-5 text-blue-500" />;
            }
            return <Clock className="w-5 h-5 text-gray-500" />;
          })()}
        </div>
        <div className="flex-1 min-w-0">
          {renderActivityContent}
          <span className="text-sm font-medium text-gray-900">{activity?.job?.title}</span>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => handleLike(activity?.id, activity?.liked)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border-none text-sm font-medium transition-colors ${
                activity?.liked
                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Heart className={`h-4 w-4 ${activity?.liked ? 'fill-current' : ''}`} />
              {t('activity.like')}
            </button>
            <ReferAccessButtonTooltip blocked={blocked} tooltipText={tooltipMessage}>
              <button
                type="button"
                onClick={() => navigateToJob(activity?.job?.uuid)}
                disabled={blocked}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border-none text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <img src={tableIconUserShare} alt={t('activity.refer')} className="w-4 h-4" />
                {t('activity.refer')}
              </button>
            </ReferAccessButtonTooltip>
          </div>
          <div className="text-sm mt-2 text-[#445568] flex-shrink-0">{getDateTimeFromNow(activity?.date)}</div>
        </div>
      </div>
    </div>
  );
};
